// src/v3/ui/_common/hooks/use-events.ts

import type { ProgressEvent } from "@signals/shared";
import { useEffect, useState } from "react";
import { z } from "zod";

const progressEventSchema = z.object({
  type: z.string(),
  progress: z.number().optional().default(0),
  message: z.string().optional(),
  data: z.unknown().optional(),
});

export function useEvents(eventTypes?: string[]) {
  const [events, setEvents] = useState<ProgressEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (typeof EventSource === "undefined") return;
    const es = new EventSource("/api/events");

    es.onopen = () => setIsConnected(true);
    es.onerror = () => setIsConnected(false);

    es.onmessage = (e) => {
      try {
        const result = progressEventSchema.safeParse(JSON.parse(e.data));
        if (!result.success) return;
        const parsed: ProgressEvent = result.data;
        if (
          eventTypes &&
          !eventTypes.includes(parsed.type) &&
          !eventTypes.includes("*")
        ) {
          return;
        }
        setEvents((prev) => [...prev, parsed]);
      } catch {
        // ignore non-JSON messages (heartbeats)
      }
    };

    return () => {
      es.close();
    };
  }, [eventTypes?.join(",")]);

  const clearEvents = () => setEvents([]);

  return { events, isConnected, clearEvents };
}
